import { Box, Button, Typography, Card } from "@mui/material";
import { ProcessExecutionResponse, StepExecutionResponse } from "api/main/data-contracts";
import { adminTheme } from "theme/adminTheme";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { InfoComponent } from "../InfoComponent";

interface Props {
  process: ProcessExecutionResponse;
  step: StepExecutionResponse;
  execute: (obj: object) => Promise<void>;
  refresh: () => Promise<void>;
  previous: () => void;
  next: () => void;
  canGoNext: boolean;
  canGoBack: boolean;
  canExecute: boolean;
}

export default function GenerateDocumentExecution({
  step,
  execute,
  next,
  previous,
  canGoBack,
  canGoNext,
  canExecute,
}: Props) {
  const isStepActive = step.status === "ACTIVE";
  const isStepFinished = step.status === "FINISHED";
  const isStepNotActive = step.status === "NOT_ACTIVE";

  const handleExecute = async () => {
    await execute({});
  };

  if (isStepNotActive) {
    return (
      <InfoComponent
        IconComponent={AccessTimeIcon}
        waiting={true}
        title="Prašome atlikti veiksmus esančius prieš tai"
        text="Dokumentas bus sugeneruotas, kai atliksite visus prašymo veiksmus"
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
      <Card
        sx={{
          borderRadius: adminTheme.borderRadius.lg,
          border: `1px solid ${adminTheme.colors.border}`,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${adminTheme.colors.info}08 0%, ${adminTheme.colors.background} 100%)`,
            borderBottom: `1px solid ${adminTheme.colors.border}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: adminTheme.borderRadius.md,
              background: `linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <DescriptionIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}>
              Dokumento generavimas
            </Typography>
            <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
              {step.step?.description || "Dokumentas bus sugeneruotas pagal pateiktus duomenis"}
            </Typography>
          </Box>
          {isStepFinished && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: `${adminTheme.colors.success}15`,
                borderRadius: adminTheme.borderRadius.md,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: adminTheme.colors.success }} />
              <Typography variant="body2" sx={{ color: adminTheme.colors.success, fontWeight: 600 }}>
                Sugeneruota
              </Typography>
            </Box>
          )}
        </Box>

        {/* Card Content */}
        <Box sx={{ p: 3 }}>
          {isStepActive && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <DescriptionIcon sx={{ fontSize: 64, color: adminTheme.colors.primary, opacity: 0.5, mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary, mb: 1 }}>
                Dokumentas paruoštas generavimui
              </Typography>
              <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary, mb: 3 }}>
                Paspauskite mygtuką žemiau, kad sugeneruotumėte dokumentą
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleExecute}
                disabled={!canExecute}
                sx={{
                  background: adminTheme.gradients.primary,
                  "&:hover": { background: adminTheme.gradients.primaryHover },
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                }}
              >
                Generuoti dokumentą
              </Button>
            </Box>
          )}

          {isStepFinished && (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: adminTheme.colors.success, mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary, mb: 1 }}>
                Dokumentas sėkmingai sugeneruotas
              </Typography>
              <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
                Galite tęsti į kitą žingsnį
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => previous()}
          disabled={!canGoBack}
          sx={{
            flex: 1,
            borderColor: adminTheme.colors.border,
            color: adminTheme.colors.textSecondary,
            py: 1.5,
            "&:hover": {
              borderColor: adminTheme.colors.primary,
              bgcolor: `${adminTheme.colors.primary}08`,
            },
          }}
        >
          Ankstesnis
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => next()}
          disabled={!canGoNext}
          sx={{
            flex: 1,
            background: adminTheme.gradients.primary,
            "&:hover": { opacity: 0.9 },
            py: 1.5,
            fontWeight: 600,
          }}
        >
          Kitas
        </Button>
      </Box>
    </Box>
  );
}
